import { html, render } from 'lit-html'
import { ifDefined } from 'lit-html/directives/if-defined'
import { capitalize } from '@polleverywhere/core/lib/util'
import { register } from '@polleverywhere/hmr-custom-element'
import '@polleverywhere/icon-element'
import './style.scss'

/**
 * Configuration of tabs as they should be rendered top/down or left/right.
 * @type {Array.<Object>}
 * @param {string} name Unique identifer and tab label, e.g. `home` => `Home`
 * @param {Object} icon Config for `<icon-element>`
 * @param {string} icon.type Icon type.
 * @param {string} icon.name Icon name.
 */
const Tabs = {
  presenter: [{
    name: 'home',
    icon: {
      type: 'legacy-glyph',
      name: 'logo'
    }
  }, {
    name: 'activities',
    icon: {
      type: 'legacy-glyph',
      name: 'training'
    }
  }, {
    name: 'reports',
    icon: {
      type: 'glyph',
      name: 'report-empty'
    }
    // }, {
    //   name: 'participate',
    //   icon: {
    //     type: 'glyph',
    //     name: 'convert'
    //   }
  }, {
    name: 'settings',
    icon: {
      type: 'glyph',
      name: 'gear'
    }
  }],
  participant: [{
    name: 'home',
    icon: {
      type: 'legacy-glyph',
      name: 'logo'
    }
  }, {
    name: 'history',
    route: 'response_history',
    icon: {
      type: 'glyph',
      name: 'response-history'
    }
  }, {
    name: 'registration',
    route: 'registrations',
    icon: {
      type: 'glyph',
      name: 'registration'
    }
  }, {
    name: 'present',
    icon: {
      type: 'glyph',
      name: 'activity'
    }
  }, {
    name: 'settings',
    icon: {
      type: 'glyph',
      name: 'gear'
    }
  }, {
    name: 'log in',
    icon: {
      type: 'glyph',
      name: 'profile'
    }
  }]
}

function iconTemplate (tab) {
  return html`
    <icon-element
      type='${ifDefined(tab.icon.type)}'
      name='${ifDefined(tab.icon.name)}'
    ></icon-element>
  `
}

function template (tabs) {
  return html`
    <nav data-count=${tabs.length}>
    ${tabs.map((tab) => html`
      <button name='${tab.name}'
        title='${capitalize(tab.name)}'
        aria-label='${capitalize(tab.name)}'
        data-route='${tab.route || tab.name}'
        ?active=${tab.active}
        @click=${handleClick}>
        ${iconTemplate(tab)}
      </button>
    `)}
    </nav>
  `
}

function handleClick (e) {
  const name = e.currentTarget.name
  const route = e.currentTarget.getAttribute('data-route')

  const clickEvent = new CustomEvent('click:tab', {
    bubbles: true,
    detail: { name, route }
  })

  this.dispatchEvent(clickEvent)

  if (this.getAttribute('active') !== name) {
    // Only trigger update when switching to non-active tab.
    this.setAttribute('active', name)
    renderTemplate.call(this)

    const event = new CustomEvent('change:active', {
      bubbles: true,
      detail: { name, route }
    })
    this.dispatchEvent(event)
  }
}

function renderTemplate () {
  if (!this.isConnected) return

  const active = this.getAttribute('active') || undefined
  const hiddenTabs = this.hasAttribute('hide')
    ? this.getAttribute('hide').split(',')
    : []

  // default to presenter tabs
  const tabs = Tabs[this.getAttribute('profile')] || Tabs.presenter

  // Generate a new config object for each tab containing all metadata used to
  // render each tab template.
  let tabsConfig = tabs.map((tab) => Object.assign({}, tab, {
    active: tab.name === active
  }))

  // Filter out any tabs that should be hidden.
  tabsConfig = tabsConfig.filter((t) => !hiddenTabs.includes(t.name))

  render(template(tabsConfig), this, {
    eventContext: this
  })
}

/**
 * TabBar renders a responsive navigation bar that renders in wide views along
 * the left edge spanning the full vertical height. In tiny mobile widths, the
 * bar renders along the bottom edge.
 *
 * @element tab-bar
 *
 * @fires change:active - Fires whenever the active tab changes.
 * @fires click:tab - Fires whenever the user clicks a tab.
 *
 * @attr {string} active - Sets the specified tab name as active.
 * @attr {string} hide - Comma separated list of tab names to hide.
 * @attr {string} [profile='presenter'] - Which tab profile to use
 *
 * @cssprop --active-color - Set active tab highlight color
 * @cssprop --background-color - Set background color
 * @cssprop --color - Set foreground color
 * @cssprop --divider-color - Set tab bar dividing border color
 * @cssprop --font-size - Set font size for largest viewport
 * @cssprop --mobile-font-size - Set font size for small mobile viewports
 */
export default class TabBar extends HTMLElement {
  static get tagName () { return 'tab-bar' }
  static get observedAttributes () {
    return ['active', 'hide', 'profile']
  }

  connectedCallback () {
    renderTemplate.call(this)
  }

  attributeChangedCallback () {
    renderTemplate.call(this)
  }
}

register(module, TabBar.tagName, TabBar)
